import { createPortal } from 'react-dom';
import { keyframes } from '@emotion/react';
import { Box, CircularProgress, Stack, Typography } from '@mui/material';

interface ArtifactDropOverlayProps {
  patientName?: string;
  /** One-liner under the main pill — typically the upload constraints
   *  summary like "PDF, JPG, PNG up to 25 MB". */
  subtitle?: string;
  isUploading?: boolean;
  uploadingMessage?: string;
}

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.6; }
`;

/**
 * Full-viewport drop indicator. Rendered via portal to `document.body` so it
 * sits above any sticky chrome / modal overlays. Pure visual — the parent
 * component handles the actual drop event.
 */
export function ArtifactDropOverlay({
  patientName,
  subtitle,
  isUploading = false,
  uploadingMessage,
}: ArtifactDropOverlayProps) {
  const message = isUploading
    ? (uploadingMessage ?? 'Uploading files')
    : patientName
      ? `Drop to add to ${patientName}'s chart`
      : 'Drop to add to this chart';

  return createPortal(
    <>
      <Box
        sx={{
          position: 'fixed',
          inset: 12,
          border: '3px dashed',
          borderColor: 'primary.main',
          borderRadius: 2,
          backgroundColor: theme => `${theme.palette.primary.main}0a`,
          pointerEvents: 'none',
          zIndex: theme => theme.zIndex.modal + 1,
          animation: isUploading ? undefined : `${pulse} 1.2s ease-in-out infinite`,
        }}
      />
      <Stack
        spacing={0.5}
        sx={{
          alignItems: 'center',
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          backgroundColor: 'primary.main',
          color: 'primary.contrastText',
          px: 3,
          py: 1.5,
          borderRadius: 3,
          pointerEvents: 'none',
          zIndex: theme => theme.zIndex.modal + 2,
          boxShadow: 4,
          maxWidth: 480,
          textAlign: 'center',
        }}
      >
        {isUploading && (
          <CircularProgress size={22} thickness={5} sx={{ color: 'primary.contrastText' }} />
        )}
        <Typography variant="subtitle2" sx={{ fontWeight: 700, letterSpacing: 0.3 }}>
          {message}
        </Typography>
        {subtitle && (
          <Typography variant="caption" sx={{ opacity: 0.85 }}>
            {subtitle}
          </Typography>
        )}
      </Stack>
    </>,
    document.body,
  );
}
